import gql from 'graphql-tag';

import { executeGqlMutation } from 'src/graphql/client';
import { FRAGMENT_JOB_ID, waitForJobWithEntityResult } from 'src/graphql/job';
import { getGqlFragmentName, getSingleProp } from 'src/lib/graphql';
import type {
  BackendLongFormBuilderOutlineSection,
  BackendMutationGenerateLongFormBuilderSectionArgs,
} from 'src/types/backend';

import { FRAGMENT_PROJECT_LONG_FORM_SECTION } from './schema';

type Variables = Omit<BackendMutationGenerateLongFormBuilderSectionArgs, 'async'>;
type Result = BackendLongFormBuilderOutlineSection;

interface Options {
  async?: boolean;
  abortSignal?: AbortSignal;
}

export const generateLongFormSection = async (
  {
    projectId,
    sectionId,
    assetType,
    generationTool,
    instruction,
    promptInstructionsData,
    urlExtractions,
    mediaGenerationParameters,
    newSection,
    debugAcPrompt,
  }: Variables,
  options: Options | null = null
) => {
  const resultFragment = FRAGMENT_PROJECT_LONG_FORM_SECTION;
  const fragment = options?.async ? FRAGMENT_JOB_ID : resultFragment;
  const mutation = gql`
    mutation GenerateLongFormBuilderSection(
      $async: Boolean
      $projectId: ID!
      $sectionId: ID!
      $assetType: AssetTypeEnum!
      $generationTool: GenerationToolEnum!
      $instruction: Instruction
      $promptInstructionsData: PromptInstructionData
      $urlExtractions: [UrlExtractionInput!]
      $mediaGenerationParameters: MediaGenerationParametersInput
      $newSection: Boolean
      $debugAcPrompt: String
    ) {
      generateLongFormBuilderSection(
        async: $async
        projectId: $projectId
        sectionId: $sectionId
        assetType: $assetType
        generationTool: $generationTool
        instruction: $instruction
        promptInstructionsData: $promptInstructionsData
        urlExtractions: $urlExtractions
        mediaGenerationParameters: $mediaGenerationParameters
        newSection: $newSection
        debugAcPrompt: $debugAcPrompt
      ) {
        ...${getGqlFragmentName(fragment)}
      }
    }
    ${fragment}
  `;

  const variables = {
    async: options?.async ?? false,
    projectId,
    sectionId,
    assetType,
    generationTool,
    instruction,
    promptInstructionsData,
    urlExtractions,
    mediaGenerationParameters,
    newSection,
    debugAcPrompt,
  };

  const response = await executeGqlMutation({
    mutation,
    variables,
    options: {
      abortSignal: options?.abortSignal,
      traceAcRequests: true,
    },
  });

  const result = getSingleProp<Result>(response.data);
  if (!options?.async || !result) {
    return result;
  }

  return waitForJobWithEntityResult<Result>(result.id, resultFragment, {
    abortSignal: options?.abortSignal,
  });
};
